import React from 'react';
import { Link } from 'react-router-dom';
import { Video, CheckCircle2, Clock, Camera, Users, Tv, ArrowRight, Play, DollarSign, Eye, Award, Zap, TrendingUp } from 'lucide-react';

const AIClonePage = () => {
  const cloneFeatures = [
    { icon: <CheckCircle2 size={24} />, text: "Fully done FOR you (not taught to you)" },
    { icon: <Camera size={24} />, text: "Professional studio filming" },
    { icon: <DollarSign size={24} />, text: "$150,000+ in equipment" },
    { icon: <Users size={24} />, text: "Team with 20+ clones of experience" },
    { icon: <Tv size={24} />, text: "Multiple sets and green screen" },
    { icon: <Clock size={24} />, text: "Clone ready in 3 days" }
  ];

  const cloneCreates = [
    "Daily social media content across all platforms",
    "Complete online courses (10-15 hours of content)",
    "High-converting video ads for Facebook, Instagram, TikTok, YouTube",
    "Sales videos that close deals while you sleep",
    "YouTube content that builds your audience 24/7",
    "Email marketing videos and VSLs",
    "Webinar presentations and replays",
    "Customer onboarding and training videos"
  ];

  const cloneStats = [
    { number: "5M+", label: "Views per month" },
    { number: "$4,000", label: "Per video on brand deals" },
    { number: "2,000+", label: "Sales from one video" },
    { number: "500+", label: "Hours saved annually" }
  ];

  return (
    <div className="dark-page ai-clone-page">
      {/* Hero Section */}
      <section className="tradelines-hero ai-clone-hero">
        <div className="tradelines-hero-content">
          <div className="tradelines-badge">DONE-FOR-YOU SERVICE</div>
          <h1 className="display-huge tradelines-title">
            <span className="text-gradient">AI Clone</span> Creation
          </h1>
          <p className="body-large tradelines-subtitle">
            Fly in. Sit down. We do it for you. Your AI clone, built in just 4 hours.<br />
            No courses to figure out. No confusing software. No wasted time learning.<br />
            This is what happens when you stop trying to do it yourself and let professionals handle it.
          </p>
          
          <div className="tradelines-features-row">
            <div className="tradeline-feature-item">
              <Video size={24} />
              <span>4-Hour Studio Session</span>
            </div>
            <div className="tradeline-feature-item">
              <Clock size={24} />
              <span>Clone Ready in 3 Days</span>
            </div>
            <div className="tradeline-feature-item">
              <Camera size={24} />
              <span>$150K+ Equipment</span>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Banner */}
      <div className="guarantee-banner">
        <div className="guarantee-content">
          <Play size={24} />
          <p>After your clone is built, you never have to film again — your clone does ALL the content creation</p>
        </div>
      </div>

      {/* How It Works */}
      <section className="position-explainer">
        <div className="section-header">
          <h2 className="display-medium section-title">How It Works</h2>
          <p className="body-medium section-description">
            Simple. Done. Professional. No learning curve required.
          </p>
        </div>
        
        <div className="process-steps">
          <div className="process-step">
            <div className="process-step-number">1</div>
            <div className="process-step-content">
              <h3>Book Your Flight & Show Up</h3>
              <p>You book a flight to our studio location. That's literally all you need to do. No prep work. No scripts to write. No equipment to buy. Just show up ready to be yourself on camera for a few hours.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">2</div>
            <div className="process-step-content">
              <h3>4-Hour Professional Studio Session</h3>
              <p>Our expert team spends 4 hours capturing everything we need using $150,000+ in professional-grade equipment. Multiple camera angles. Multiple sets. Green screen capabilities. Professional lighting and audio. We've perfected this process over 20+ clone builds.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">3</div>
            <div className="process-step-content">
              <h3>Fly Home & Receive Your Clone</h3>
              <p>You fly home and get back to running your business. Three days later, your AI clone is ready and delivered. From that moment forward, you never have to sit in front of a camera again. Your clone handles ALL video content creation — forever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="tradelines-grid-section">
        <div className="section-header">
          <h2 className="display-medium section-title">What Makes This Different</h2>
          <p className="body-medium section-description">
            Professional equipment. Experienced team. Real results.
          </p>
        </div>
        
        <div className="ai-clone-features-grid">
          {cloneFeatures.map((feature, idx) => (
            <div key={idx} className="ai-clone-feature-card">
              <div className="ai-clone-feature-icon">{feature.icon}</div>
              <span className="ai-clone-feature-text">{feature.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What Your Clone Creates */}
      <section className="ai-clone-creates-section">
        <div className="section-header">
          <h2 className="display-medium section-title">Your Clone Creates</h2>
          <p className="body-medium section-description">
            Unlimited content without you ever filming again
          </p>
        </div>
        
        <div className="ai-clone-creates-grid">
          {cloneCreates.map((item, idx) => (
            <div key={idx} className="ai-clone-creates-item">
              <CheckCircle2 size={20} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="ai-clone-stats-section">
        <div className="section-header">
          <h2 className="display-medium section-title">Real Clone Results</h2>
          <p className="body-medium section-description">
            Numbers from our actual AI clones in production
          </p>
        </div>
        
        <div className="ai-clone-stats-grid">
          {cloneStats.map((stat, idx) => (
            <div key={idx} className="ai-clone-stat-card">
              <div className="ai-clone-stat-number">{stat.number}</div>
              <div className="ai-clone-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="ai-clone-quality-note">
          <Award size={32} />
          <p>The quality? People genuinely can't tell it's not real. Even family members tested it and couldn't tell the difference. That's what happens when you use professional equipment and a team that's perfected the process over a year of testing.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ai-clone-pricing-section">
        <div className="business-tradeline-container">
          <div className="business-tradeline-card ai-clone-pricing-card">
            <div className="ai-clone-cta-header">
              <Zap size={48} className="ai-clone-cta-icon" />
              <h3 className="business-tradeline-title">Ready to Clone Yourself?</h3>
            </div>
            
            <p className="business-tradeline-description">
              Think about how much time you spend filming content every week. 10 hours? 15 hours? 20 hours? Now imagine redirecting ALL of that time to closing deals, scaling your business, spending time with family, or literally anything else you'd rather be doing. Your clone works 24/7, never gets tired, never has a bad hair day, and produces content that's indistinguishable from the real you. This isn't some cheap deepfake — this is professional-grade AI cloning built by a team that's perfected the process. One 4-hour session. Three days later, you have a clone that creates unlimited content forever.
            </p>
            
            <div className="business-tradeline-features ai-clone-pricing-features">
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>4-Hour Professional Studio Session</span>
              </div>
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>$150,000+ Professional Equipment</span>
              </div>
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>Multiple Sets & Green Screen Options</span>
              </div>
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>Team With 20+ Clone Builds Experience</span>
              </div>
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>Clone Delivered in 3 Days</span>
              </div>
              <div className="business-feature-item">
                <CheckCircle2 size={18} />
                <span>Never Film Content Again — Forever</span>
              </div>
            </div>

            <a 
              href="https://www.fanbasis.com/agency-checkout/THEACADEMYCEO/jZvDl"
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary business-tradeline-btn"
              data-testid="ai-clone-buy-btn"
            >
              Learn More
              <ArrowRight size={20} />
            </a>

            <p className="ai-clone-limited-note">
              Limited spots available — we only take on a handful of clients each month
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="tradelines-cta-section">
        <div className="tradelines-cta-content">
          <h2 className="display-medium">Have Questions About AI Cloning?</h2>
          <p className="body-large">
            Not sure if this is right for you? Book a quick strategy call and we'll walk you through 
            exactly how it works, show you real examples of clones we've built, and help you understand 
            how much time and money you'll save.
          </p>
          <a 
            href="https://calendly.com/thecollinsacademynew/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary"
          >
            Schedule Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default AIClonePage;
